import { Flex, Skeleton, Table, Text } from '@radix-ui/themes'
import {
  PagePamDocumentQueueSummary,
  PamDocumentQueueSummary,
  PamPlayerVerificationStatus,
} from '@Generated/back-office-client/src'
import { Pagination } from '@Components/common/pagination'
import { LoadingRows } from '@Components/util/loading-rows'
import { formatDateToMonthDayYearString } from '@Components/util/utils'
import { PamVerificationStatusBadge } from './pam-verification-status-badge'
import './styles.scss'

export type PamDocumentQueueViewProps = {
  pageSize: number
  currentPage: number
  isLoading: boolean
  onPageChange: (page: number) => void
  onRowClick: (row: PamDocumentQueueSummary) => void
  page?: PagePamDocumentQueueSummary
}

const formatName = (row: PamDocumentQueueSummary): string => {
  const name = [row.firstName, row.lastName].filter((part) => Boolean(part)).join(' ')
  if (!name) {
    return '—'
  }
  return name
}

export const PamDocumentQueueView = ({
  page,
  pageSize,
  currentPage,
  isLoading,
  onPageChange,
  onRowClick,
}: PamDocumentQueueViewProps) => {
  const rows = page?.results.map((row) => {
    return (
      <Table.Row
        key={row.pamPlayerId}
        onClick={() => onRowClick(row)}
        style={{ cursor: 'pointer' }}>
        <Table.Cell>
          <PamVerificationStatusBadge
            status={row.verificationStatus ?? PamPlayerVerificationStatus.not_started}
          />
        </Table.Cell>
        <Table.Cell>{formatName(row)}</Table.Cell>
        <Table.Cell>{row.email ?? '—'}</Table.Cell>
        <Table.Cell>{row.documentCount}</Table.Cell>
        <Table.Cell>{formatDateToMonthDayYearString(row.firstDocumentAt)}</Table.Cell>
      </Table.Row>
    )
  })

  const isEmpty = !isLoading && page !== undefined && page.results.length === 0

  return (
    <Flex direction="column" gap="4">
      <Skeleton loading={isLoading}>
        <Table.Root variant="surface" className="pam-verification-table">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Documents</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Waiting since</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{isLoading ? <LoadingRows /> : rows}</Table.Body>
        </Table.Root>
      </Skeleton>

      {isEmpty && <Text color="gray">No players waiting for document review.</Text>}

      {!isLoading && page !== undefined && (
        <Pagination
          totalItems={page.totalResults}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={onPageChange}
        />
      )}
    </Flex>
  )
}
